(function compactShopifyChatLauncher() {
  const CHAT_SELECTOR = '#ShopifyChat';
  const STYLE_ID = 'ab-compact-shopify-chat-launcher';
  const DESKTOP_QUERY = '(min-width: 750px)';
  const MOBILE_QUERY = '(max-width: 749px)';
  const COMPACT_HOST_SIZE = '52px';
  const COMPACT_BUTTON_SIZE = '44px';
  const COMPACT_ICON_SIZE = '36px';
  const MOBILE_STICKY_CLEARANCE_PX = 8;
  const STICKY_SELECTORS = [
    '.sticky-add-to-cart__bar[data-stuck="true"]',
    '.ab-tiered-bundle-picker__sticky[data-visible="true"]',
    '.ab-inline-bundle-picker__sticky[data-visible="true"]',
  ];

  const desktopMedia = window.matchMedia ? window.matchMedia(DESKTOP_QUERY) : null;
  const mobileMedia = window.matchMedia ? window.matchMedia(MOBILE_QUERY) : null;
  const hostObservers = new WeakMap();
  let syncFrame = null;
  let supportCopySanitizeFrame = null;
  const OLD_MESSAGE_WORD = ['voice', 'mail'].join('');
  const CLEAN_SUPPORT_BLOCK_TEXT = 'Email support@awesomebound.com or call us toll-free at 1-888-828-3167 for support.';
  const CLEAN_SUPPORT_SECTION_TEXT = 'For questions, email support@awesomebound.com or call us toll-free at 1-888-828-3167.';
  const SUPPORT_COPY_REPLACEMENTS = [
    [
      `Email support@awesomebound.com or call our toll-free ${OLD_MESSAGE_WORD} line at 1-888-828-3167 for support.`,
      CLEAN_SUPPORT_BLOCK_TEXT,
    ],
    [
      `For questions, email support@awesomebound.com or call our toll-free ${OLD_MESSAGE_WORD} line at 1-888-828-3167.`,
      CLEAN_SUPPORT_SECTION_TEXT,
    ],
  ];

  const compactStyles = `
    :host([is-open="false"]) {
      width: ${COMPACT_HOST_SIZE} !important;
      max-width: ${COMPACT_HOST_SIZE} !important;
      height: ${COMPACT_HOST_SIZE} !important;
      max-height: ${COMPACT_HOST_SIZE} !important;
    }

    :host([is-open="false"]) .chat-app {
      display: grid !important;
      place-items: center !important;
      width: ${COMPACT_HOST_SIZE} !important;
      max-width: ${COMPACT_HOST_SIZE} !important;
      height: ${COMPACT_HOST_SIZE} !important;
      max-height: ${COMPACT_HOST_SIZE} !important;
    }

    :host([is-open="false"]) button[data-spec="toggle-button"] {
      display: flex !important;
      align-items: center !important;
      justify-content: center !important;
      width: ${COMPACT_BUTTON_SIZE} !important;
      min-width: ${COMPACT_BUTTON_SIZE} !important;
      max-width: ${COMPACT_BUTTON_SIZE} !important;
      height: ${COMPACT_BUTTON_SIZE} !important;
      min-height: ${COMPACT_BUTTON_SIZE} !important;
      max-height: ${COMPACT_BUTTON_SIZE} !important;
      padding: 0 !important;
      border-radius: 14px !important;
      position: relative !important;
    }

    :host([is-open="false"]) [data-spec="button-icon"] {
      display: block !important;
      flex: 0 0 ${COMPACT_ICON_SIZE} !important;
      width: ${COMPACT_ICON_SIZE} !important;
      min-width: ${COMPACT_ICON_SIZE} !important;
      max-width: ${COMPACT_ICON_SIZE} !important;
      height: ${COMPACT_ICON_SIZE} !important;
      min-height: ${COMPACT_ICON_SIZE} !important;
      max-height: ${COMPACT_ICON_SIZE} !important;
      margin: 0 !important;
      transform: translateX(1px) !important;
    }

    :host([is-open="false"]) [data-spec="button-text"] {
      display: none !important;
    }

    :host([is-open="false"]) [data-spec="notification-badge"] {
      position: absolute !important;
      top: -5px !important;
      right: -5px !important;
      margin: 0 !important;
    }
  `;

  function isDesktop() {
    return desktopMedia ? desktopMedia.matches : window.innerWidth >= 750;
  }

  function isMobile() {
    return mobileMedia ? mobileMedia.matches : window.innerWidth < 750;
  }

  function sanitizeSupportCopy(root = document.body) {
    if (!root) return;

    const walker = document.createTreeWalker(root, window.NodeFilter?.SHOW_TEXT || 4);
    let node = walker.nextNode();

    while (node) {
      const parentTag = node.parentElement?.tagName;

      if (!['SCRIPT', 'STYLE', 'NOSCRIPT', 'TEXTAREA'].includes(parentTag)) {
        let text = node.nodeValue || '';

        for (const [oldText, newText] of SUPPORT_COPY_REPLACEMENTS) {
          text = text.replaceAll(oldText, newText);
        }

        if (text !== node.nodeValue) {
          node.nodeValue = text;
        }
      }

      node = walker.nextNode();
    }
  }

  function scheduleSupportCopySanitize() {
    if (supportCopySanitizeFrame) return;

    supportCopySanitizeFrame = window.requestAnimationFrame(() => {
      supportCopySanitizeFrame = null;
      sanitizeSupportCopy();
    });
  }

  function observeForStaleSupportCopy() {
    scheduleSupportCopySanitize();

    const observer = new MutationObserver(() => {
      scheduleSupportCopySanitize();
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
    });

    window.setTimeout(() => {
      observer.disconnect();
    }, 15000);
  }

  function viewportHeight() {
    return window.visualViewport?.height || window.innerHeight;
  }

  function shouldCompactHost(host) {
    return host.getAttribute('is-open') === 'false';
  }

  function injectCompactStyles(shadowRoot) {
    if (!shadowRoot) return;

    const existing = shadowRoot.getElementById(STYLE_ID);

    if (existing) {
      shadowRoot.appendChild(existing);
      return;
    }

    const style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent = compactStyles;
    shadowRoot.appendChild(style);
  }

  function preserveAccessibleName(shadowRoot) {
    const toggle = shadowRoot?.querySelector('button[data-spec="toggle-button"]');

    if (toggle && !toggle.getAttribute('aria-label')) {
      toggle.setAttribute('aria-label', 'Need help?');
    }
  }

  function syncButtonText(shadowRoot, shouldCompact) {
    const buttonText = shadowRoot?.querySelector('[data-spec="button-text"]');

    if (!buttonText) return;

    if (shouldCompact) {
      buttonText.style.setProperty('display', 'none', 'important');
      buttonText.dataset.abCompactChatLauncherHidden = 'true';
    } else if (buttonText.dataset.abCompactChatLauncherHidden === 'true') {
      buttonText.style.removeProperty('display');
      delete buttonText.dataset.abCompactChatLauncherHidden;
    }
  }

  function syncNotificationBadge(host) {
    const badge = host.shadowRoot?.querySelector('[data-spec="notification-badge"]');

    if (!badge) return;

    if (host.getAttribute('is-open') === 'true') {
      host.dataset.abChatInteracted = 'true';
    }

    const badgeText = badge.textContent.trim();
    const isDefaultBadge = badgeText === '1';
    const shouldHideDefaultBadge =
      isDefaultBadge &&
      host.dataset.abChatInteracted !== 'true' &&
      host.dataset.abChatBadgeRealActivity !== 'true';

    if (badgeText && !isDefaultBadge) {
      host.dataset.abChatBadgeRealActivity = 'true';
    }

    if (shouldHideDefaultBadge) {
      badge.style.setProperty('display', 'none', 'important');
      badge.dataset.abHiddenDefaultBadge = 'true';
      return;
    }

    if (badge.dataset.abHiddenDefaultBadge === 'true') {
      badge.style.removeProperty('display');
      delete badge.dataset.abHiddenDefaultBadge;
    }
  }

  function visibleStickyBar() {
    for (const selector of STICKY_SELECTORS) {
      const bar = document.querySelector(selector);
      if (!bar) continue;

      const rect = bar.getBoundingClientRect();
      const styles = window.getComputedStyle(bar);
      const isVisible =
        rect.width > 0 &&
        rect.height > 0 &&
        rect.bottom > viewportHeight() - Math.max(96, rect.height + MOBILE_STICKY_CLEARANCE_PX) &&
        rect.top < viewportHeight() &&
        styles.display !== 'none' &&
        styles.visibility !== 'hidden' &&
        styles.opacity !== '0';

      if (isVisible) return { bar, rect };
    }

    return null;
  }

  function syncMobileStickyOffset(host) {
    if (!host || !isMobile()) {
      clearMobileOpenPin(host);

      if (host?.dataset.abStickyAtcOffset === 'true') {
        host.style.removeProperty('bottom');
        delete host.dataset.abStickyAtcOffset;
      }
      return;
    }

    if (host.getAttribute('is-open') === 'true') {
      pinMobileOpenHost(host);
      return;
    }

    clearMobileOpenPin(host);

    const visible = visibleStickyBar();

    if (!visible) {
      if (host.dataset.abStickyAtcOffset === 'true') {
        host.style.removeProperty('bottom');
        delete host.dataset.abStickyAtcOffset;
      }
      return;
    }

    const offset = Math.ceil(visible.rect.height + MOBILE_STICKY_CLEARANCE_PX);
    host.style.bottom = `calc(${offset}px + env(safe-area-inset-bottom))`;
    host.dataset.abStickyAtcOffset = 'true';
  }

  function pinMobileOpenHost(host) {
    host.style.top = '0';
    host.style.bottom = '0';
    host.dataset.abMobileOpenPinned = 'true';

    if (host.dataset.abStickyAtcOffset === 'true') {
      delete host.dataset.abStickyAtcOffset;
    }
  }

  function clearMobileOpenPin(host) {
    if (host?.dataset.abMobileOpenPinned !== 'true') return;

    host.style.removeProperty('top');
    if (host.style.bottom === '0px' || host.style.bottom === '0') {
      host.style.removeProperty('bottom');
    }
    delete host.dataset.abMobileOpenPinned;
  }

  function currentTemplateName() {
    return document.querySelector('main[data-template]')?.getAttribute('data-template') || '';
  }

  function isProductTemplate() {
    const templateName = currentTemplateName();
    return templateName === 'product' || templateName.startsWith('product.');
  }

  function isCartTemplate() {
    return currentTemplateName() === 'cart';
  }

  function isCartDrawerOpen() {
    return Boolean(document.querySelector('.cart-drawer__dialog[open]'));
  }

  function shouldHideHostForSurface() {
    return isProductTemplate() || isCartTemplate() || isCartDrawerOpen();
  }

  function clearTransientHostPosition(host) {
    clearMobileOpenPin(host);

    if (host?.dataset.abStickyAtcOffset === 'true') {
      host.style.removeProperty('bottom');
      delete host.dataset.abStickyAtcOffset;
    }
  }

  function syncHostSurfaceVisibility(host) {
    if (!host) return false;

    if (shouldHideHostForSurface()) {
      clearTransientHostPosition(host);
      host.style.setProperty('display', 'none', 'important');
      host.style.setProperty('visibility', 'hidden', 'important');
      host.style.setProperty('pointer-events', 'none', 'important');
      host.dataset.abHiddenForSurface = 'true';
      return true;
    }

    if (host.dataset.abHiddenForSurface === 'true') {
      host.style.removeProperty('display');
      host.style.removeProperty('visibility');
      host.style.removeProperty('pointer-events');
      delete host.dataset.abHiddenForSurface;
    }

    return false;
  }

  function syncHost(host) {
    if (!host) return false;

    if (syncHostSurfaceVisibility(host)) return true;

    syncMobileStickyOffset(host);

    if (!host.shadowRoot) return true;

    injectCompactStyles(host.shadowRoot);
    preserveAccessibleName(host.shadowRoot);

    const shouldCompact = shouldCompactHost(host);

    syncButtonText(host.shadowRoot, shouldCompact);
    syncNotificationBadge(host);

    if (shouldCompact) {
      host.style.width = COMPACT_HOST_SIZE;
      host.style.height = COMPACT_HOST_SIZE;
      host.dataset.abCompactChatLauncher = 'true';
    } else if (host.dataset.abCompactChatLauncher === 'true') {
      host.style.removeProperty('width');
      host.style.removeProperty('height');
      delete host.dataset.abCompactChatLauncher;
    }

    return true;
  }

  function attachHostObserver(host) {
    if (!host || hostObservers.has(host)) return;

    const observer = new MutationObserver(() => {
      syncHost(host);
    });

    observer.observe(host, {
      attributes: true,
      attributeFilter: ['is-open', 'is-mobile', 'style'],
    });

    hostObservers.set(host, observer);
  }

  function findAndSyncHost() {
    const host = document.querySelector(CHAT_SELECTOR);

    if (!host) return false;

    const synced = syncHost(host);

    if (synced) {
      attachHostObserver(host);
    }

    return synced;
  }

  function scheduleSyncHost() {
    if (syncFrame) return;

    syncFrame = window.requestAnimationFrame(() => {
      syncFrame = null;
      findAndSyncHost();
    });
  }

  function observeForLateChat() {
    const observer = new MutationObserver(() => {
      scheduleSyncHost();
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class', 'data-stuck', 'data-visible', 'hidden', 'open', 'style'],
      childList: true,
      subtree: true,
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      () => {
        findAndSyncHost();
        observeForStaleSupportCopy();
      },
      { once: true }
    );
  } else {
    findAndSyncHost();
    observeForStaleSupportCopy();
  }

  observeForLateChat();
  window.addEventListener('scroll', scheduleSyncHost, { passive: true });
  window.addEventListener('resize', scheduleSyncHost);
  window.visualViewport?.addEventListener('scroll', scheduleSyncHost, { passive: true });
  window.visualViewport?.addEventListener('resize', scheduleSyncHost);
  window.setInterval(scheduleSyncHost, 1000);

  if (desktopMedia?.addEventListener) {
    desktopMedia.addEventListener('change', scheduleSyncHost);
  } else if (desktopMedia?.addListener) {
    desktopMedia.addListener(scheduleSyncHost);
  }

  if (mobileMedia?.addEventListener) {
    mobileMedia.addEventListener('change', scheduleSyncHost);
  } else if (mobileMedia?.addListener) {
    mobileMedia.addListener(scheduleSyncHost);
  }
})();
